import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useHistory } from 'react-router';
import { Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useIoContext, useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { integrationModel } from '~/models';
import { EmailOnBehalfTypes } from '~/models/EmailOnBehalfSettingsModel';
import { getEmailProviderDetails, Tab } from '~/pages/NotificationSettings/NotificationSettings.utils';
import { selectors } from '~/store/state-selectors';
import gmailLogo from '../../../../assets/Gmail.png';
import IntegrationCard from '../IntegrationCard/IntegrationCard';
const deprecatedTestIds = {
    GmailCardTestIds: {
        Root: 'gmail-card',
        ConnectButton: 'gmail-card-connect',
        Tooltip: 'gmail-card-tooltip',
        ErrorToast: {
            StartGmailFlow: 'start-gmail-flow-error-toast',
            NoBusinessId: 'no-business-id-error-toast'
        }
    }
};
export const GetGmailCardTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds
    });
export default function GmailCard(param) {
    let { integration, isDisabled } = param;
    const { t: gmailTexts } = useLocaleKeys('integrations.availableApps.gmail');
    const { t: sharedTexts } = useLocaleKeys('integrations.availableApps.shared');
    const { GmailCardTestIds } = useMemo(()=>GetGmailCardTID(), []);
    const businessId = useStore(selectors.user.businessId);
    const { api } = useStoreActions();
    const changeLocation = useIoContext().browser.changeLocation;
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const { logBI } = useStoreActions();
    const history = useHistory();
    const handleClick = async ()=>{
        const providerDetails = getEmailProviderDetails(EmailOnBehalfTypes.Gmail);
        if (integrationModel.isLinked(integration)) {
            history.push(routes.notifications, {
                selectedTab: Tab.Client
            });
            return;
        }
        logBI(providerDetails.biCodes.connect, {
            vendorId: businessId
        });
        if (businessId) {
            startLoader();
            const result = await handleRequest(api.notification.emailOnBehalf.connectEmailOnBehalf({
                businessId,
                provider: EmailOnBehalfTypes.Gmail,
                pageUrl: window.location.origin + window.location.pathname
            }));
            if (result.success) {
                changeLocation(result.data);
            } else {
                logBI(providerDetails.biCodes.authError, {
                    vendorId: businessId
                });
                showErrorToast(gmailTexts.failedToConnectGmail(), GmailCardTestIds.ErrorToast.StartGmailFlow());
            }
            stopLoader();
        } else {
            logBI(providerDetails.biCodes.authError, {
                vendorId: businessId
            });
            showErrorToast(sharedTexts.pleaseRefreshAndTryAgain(), GmailCardTestIds.ErrorToast.NoBusinessId());
        }
    };
    return /*#__PURE__*/ _jsx(IntegrationCard, {
        testId: GmailCardTestIds.Root(),
        icon: /*#__PURE__*/ _jsx(IntegrationCard.LogoImg, {
            alt: "gmail logo",
            src: gmailLogo
        }),
        title: gmailTexts.title(),
        subTitle: gmailTexts.subTitle(),
        mainAction: /*#__PURE__*/ _jsx(IntegrationCard.MainActionButton, {
            bi: {
                name: 'connect_gmail'
            },
            onClick: handleClick,
            testId: GmailCardTestIds.ConnectButton(),
            disabled: isDisabled,
            tooltip: isDisabled ? {
                placement: 'right',
                title: /*#__PURE__*/ _jsxs("div", {
                    "data-testid": GmailCardTestIds.Tooltip(),
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            skin: "invertedStandard",
                            children: gmailTexts.onlyOneMailbox()
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            skin: "invertedStandard",
                            children: gmailTexts.disconnectOutlookFirst()
                        })
                    ]
                })
            } : undefined,
            children: integrationModel.isLinked(integration) ? sharedTexts.manageConnection() : sharedTexts.connectButton()
        })
    });
}
